import { useAuth0 } from "@auth0/auth0-react";
import React from "react";
import * as Router from "react-router-dom";
import { Outlet } from "react-router-dom";
import { AmplitudeUserProfileComponent } from "src/components/Amplitude/AmplitudeUserProfileComponent";
import { AuthorizationProvider } from "src/components/Providers/AuthorizationProvider";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import useAccessToken from "src/hooks/useAccessToken";
import { GlossaryContextProvider } from "src/hooks/useGlossary";
import { useLoginMethods } from "src/hooks/useLoginMethods";
import { OrganizationProvider } from "src/hooks/useOrganization";
import { useOrganizationPath } from "src/hooks/useOrganizationPath";
import { useOrgConfig } from "src/hooks/useOrgConfig";
import { UserPermissionsContextProvider } from "src/hooks/useUserPermissions";
import { useEnv } from "src/services/env";
import { isNullOrUndefined } from "src/services/predicates";
import * as Url from "src/services/url";
import { Status } from "src/types/authData";
import {
  EXPLORE_SCHOOLS_TOKEN,
  LOGIN_PREFILL_TOKEN_PARAM,
} from "src/types/searchParams";
import { LoginError } from "../Feedback/LoginError";
import { ApolloProviderWithCredential } from "../Providers/ApolloProviderWithCredential";
import { useBranding } from "../Providers/BrandingProvider";
import { QueryClientProviderWithCredential } from "../Providers/QueryClientProviderWithCredential";
import { useIsParentStorage } from "./useIsParentStorage";
import { useRedirectToLandingPage } from "./useRedirectToLandingPage";

export function RequireAuth() {
  try {
    const isParentStorage = useIsParentStorage();
    const { loginWithRedirect } = useAuth0();
    const accessToken = useAccessToken();
    const location = Router.useLocation();
    const params = Router.useParams();
    const [searchParams, setSearchParams] = Router.useSearchParams();
    const isParent = Url.isParentPortalPath(location.pathname);
    const loginMethods = useLoginMethods(isParent ? "parent" : "admin");
    const accountLookupConfig =
      useOrgConfig<OrgConfig.AccountLookupConfig>("AccountLookup");
    const org = useOrganizationPath();
    const [showLoginError, setShowLoginError] = React.useState(false);
    const branding = useBranding();
    const env = useEnv();
    const landingPageConfigRD =
      useOrgConfig<OrgConfig.LandingPageConfig>("LandingPage");

    const exploreSavedSchoolRefIDs = decodeURIComponent(
      searchParams.get(EXPLORE_SCHOOLS_TOKEN) ?? ""
    )
      .split(",")
      .filter((x) => x);

    // Prefill token is only for the unauthenticated → Auth0 hop; drop it once signed in.
    React.useEffect(() => {
      if (
        accessToken.status === Status.UNAUTHENTICATED ||
        accessToken.status === Status.LOADING
      ) {
        return;
      }
      if (!searchParams.has(LOGIN_PREFILL_TOKEN_PARAM)) {
        return;
      }
      const next = new URLSearchParams(searchParams);
      next.delete(LOGIN_PREFILL_TOKEN_PARAM);
      setSearchParams(next, { replace: true });
    }, [accessToken.status, searchParams, setSearchParams]);

    const redirectToLandingPage = useRedirectToLandingPage();
    React.useEffect(() => {
      async function redirectIfUnauthenticated() {
        if (!accountLookupConfig.hasData()) {
          return;
        }

        const accountLookupDisabled = isNullOrUndefined(
          accountLookupConfig.data
        )
          ? true
          : Boolean(accountLookupConfig.data.disabled);

        const accountLookupUrl =
          org !== undefined && !accountLookupDisabled
            ? `${window.location.origin}${Url.Parent.AccountLookup.index({
                path: org,
              })}`
            : undefined;

        if (accessToken.status === Status.UNAUTHENTICATED) {
          if (!landingPageConfigRD.hasData()) {
            return;
          }

          if (
            landingPageConfigRD.data?.disabled === false &&
            redirectToLandingPage()
          ) {
            return;
          }

          if (loginMethods.hasError()) {
            console.error(
              `Fatal error, unable to get Login config: ${loginMethods.error}`
            );
            setShowLoginError(true);
            return;
          }

          if (!loginMethods.hasData()) {
            return;
          }

          const page = searchParams.get("page") ?? undefined;
          const method = searchParams.get("method") ?? undefined;
          const hide = searchParams.get("hide") ?? undefined;
          const tk = searchParams.get(LOGIN_PREFILL_TOKEN_PARAM) ?? undefined;

          // passing avelaOrganization to scope for pre-registration flow
          // as documented here: https://community.auth0.com/t/receive-params-in-pre-user-registration-action/123340
          const scope = `avelaOrganization:${org}`;

          isParentStorage.setValue(isParent);

          // Still send `tk` to Auth0 for prefill, but don't bounce back with it.
          const returnToUrl = new URL(
            `${location.pathname}${location.search}`,
            window.location.origin
          );
          returnToUrl.searchParams.delete(LOGIN_PREFILL_TOKEN_PARAM);
          const returnTo = `${returnToUrl.pathname}${returnToUrl.search}`;

          await loginWithRedirect({
            loginSource: window.location.hostname,
            avelaOrganization: org,
            scope,
            page,
            method,
            hide,
            tk,
            // Only needed to redeem the prefill token on the login page.
            formServiceBaseUrl: tk ? env.VITE_FORM_SERVICE_BASE_URL : undefined,
            isParent: Url.isParentPortalPath(location.pathname),
            loginMethods: JSON.stringify(loginMethods.data),
            accountLookupUrl,
            branding: JSON.stringify(branding),
            hasExploreSavedSchools: !!exploreSavedSchoolRefIDs.length,
            appState: {
              returnTo,
            },
          });
        }
      }
      redirectIfUnauthenticated();
    }, [
      accessToken.status,
      loginWithRedirect,
      location,
      params,
      loginMethods,
      searchParams,
      exploreSavedSchoolRefIDs,
      accountLookupConfig,
      org,
      branding,
      isParentStorage,
      isParent,
      env.VITE_FORM_SERVICE_BASE_URL,
      redirectToLandingPage,
      landingPageConfigRD,
    ]);

    if (showLoginError) {
      return <LoginError />;
    }

    return (
      <QueryClientProviderWithCredential>
        <ApolloProviderWithCredential>
          <OrganizationProvider>
            <AuthorizationProvider>
              <UserPermissionsContextProvider>
                <GlossaryContextProvider>
                  <AmplitudeUserProfileComponent>
                    <Outlet />
                  </AmplitudeUserProfileComponent>
                </GlossaryContextProvider>
              </UserPermissionsContextProvider>
            </AuthorizationProvider>
          </OrganizationProvider>
        </ApolloProviderWithCredential>
      </QueryClientProviderWithCredential>
    );
  } catch (e) {
    console.error("Error in RequireAuth:", e);
    throw e;
  }
}
